import { useCallback, useMemo } from 'react';
import {
  useQuery,
  MutationTuple,
  useMutation,
  MutationUpdaterFn,
  Reference,
  useLazyQuery,
  QueryResult,
  LazyQueryResultTuple,
  ApolloError
} from '@apollo/client';
import {
  IApiBidsData,
  IBidsSearchArgs,
  IBidsData,
  IBidIdentifier,
  ILinkedBidIdentifier,
  IApiShortBid,
  IBidsCached,
  IBidSorting
} from './types';
import { CREATE_BIDS, UPDATE_BIDS_WS, UPDATE_BID_STATUS, GET_BIDS, GET_BID, GET_TOTAL_BIDS } from './queries';
import { useActiveWsIdState, useWorkspaces, clearWsStatsCache } from 'src/models/workspace/hooks';
import { BidsData } from '.';
import { IWorkspace } from 'src/models/workspace/types';
import { IWorkspaceStatus } from 'src/models/workspace/WorkspaceStatus/types';
import { BidItemType } from '@tendium/prom-types/tender';
import { groupBidsByWs } from './helpers';
import useBidsSearchArgs from 'src/reactiveVars/BidsSearchArgsVar';
import { ITendersCached } from 'src/models/procurements/types';
import { useUnAssignMailsToBid } from '../BidsSelector/hooks';
import { useActiveMpIdState } from 'src/models/matchingProfiles/hooks';
import { useTranslation } from 'react-i18next';
import groupBy from 'lodash/groupBy';
import { trackMoveItemsToWorkspace } from 'src/segment/events';
import { notification } from 'src/common';
import { notUndefined } from 'src/helpers/typescript';
import { EmailStatus } from 'src/models/callOffs/types';
import { useSetRequestSettings } from 'src/models/users/RequestSettings/hooks';
import { RequestType } from 'src/models/users/RequestSettings/types';

export interface IApiGetBidsResponse {
  getBids: IApiBidsData;
}

export interface IApiGetTotalBidsResponse {
  getBids: { totalBids: number };
}

export interface ISearchBidsRequest extends IBidsSearchArgs {
  wsId: string;
  stageIds?: string[];
}

export function useStagesBids(params?: {
  amount?: number;
  stageId?: string;
}): Omit<QueryResult<IApiGetBidsResponse, ISearchBidsRequest>, 'data'> & { data?: IBidsData } {
  const [wsId] = useActiveWsIdState();
  const stageId = params?.stageId;
  const amount = params?.amount;
  const [{ amount: defaultAmount, ...restSearchArgs }] = useBidsSearchArgs();
  const query = useQuery<IApiGetBidsResponse, ISearchBidsRequest>(GET_BIDS, {
    variables: {
      wsId: wsId || '',
      stageIds: stageId ? [stageId] : undefined,
      amount: amount || defaultAmount,
      ...restSearchArgs
    },
    skip: !wsId
  });

  return useMemo(() => ({ ...query, data: query.data ? new BidsData(query.data.getBids) : undefined }), [query]);
}

export function useTotalStagesBids(): LazyQueryResultTuple<IApiGetTotalBidsResponse, ISearchBidsRequest> {
  return useLazyQuery<IApiGetTotalBidsResponse, ISearchBidsRequest>(GET_TOTAL_BIDS);
}
export interface IGetBidRequest {
  bidId: string;
}
export interface IGetBidResponse {
  getBidV2: IApiShortBid;
}

export function useBidWorkspace(bidId?: string): {
  workspace: IWorkspace | null;
  status: IWorkspaceStatus | null;
  didFail: boolean;
  inNoAccessWorkspace: boolean;
} {
  const { data: wssData } = useWorkspaces();
  const { data: bidData } = useQuery<IGetBidResponse, IGetBidRequest>(GET_BID, {
    variables: { bidId: bidId || '' },
    skip: !bidId,
    fetchPolicy: 'cache-first'
  });

  const bidWsId = bidData?.getBidV2?.workspace.id;
  const statusWsId = bidData?.getBidV2?.status.id;

  const workspace = useMemo(
    () => (!!bidWsId && wssData && wssData.find(ws => ws.id === bidWsId)) || null,
    [bidWsId, wssData]
  );

  const inNoAccessWorkspace = useMemo(
    () => (!!bidWsId && wssData && !wssData.find(ws => ws.id === bidWsId)) || false,
    [bidWsId, wssData]
  );

  const status = useMemo(
    () => (workspace && workspace.statuses.find(wsStatus => wsStatus.id === statusWsId)) || null,
    [statusWsId, workspace]
  );

  const didFail = useMemo(() => bidData?.getBidV2 === null, [bidData]);

  return useMemo(() => {
    return { workspace, status, didFail, inNoAccessWorkspace };
  }, [didFail, inNoAccessWorkspace, status, workspace]);
}

interface ICreateBidsResponse {
  __typename: 'Mutation';
  createBids: IApiShortBid[] & {
    __typename: 'BidV2';
  };
}

interface CreateBidItemInput {
  id?: string;
  name?: string;
}
interface ICreateBidsRequest {
  workspaceId: string;
  itemType: BidItemType;
  items: CreateBidItemInput[];
}
export function useCreateBids(): MutationTuple<ICreateBidsResponse, ICreateBidsRequest> {
  return useMutation(CREATE_BIDS);
}
export function getUpdateCacheOnCreateBids(
  originIds: string[],
  originType: BidItemType,
  wsId: string,
  mpId?: string
): MutationUpdaterFn<ICreateBidsResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    let originIdsData = originIds;
    const { createBids: newBids } = data;
    if (originType === BidItemType.Manual) {
      originIdsData = newBids.map(bid => bid.itemId);
    }
    let origins: string[] = [];
    originIdsData.forEach(originId => {
      if (originType === BidItemType.Procurement) {
        const procRef = cache.identify({
          __typename: 'Tender',
          id: originId
        });
        const newBid = newBids.find(bid => bid.itemId === originId);
        origins = newBid?.itemId ? [...origins, newBid?.itemId] : origins;
        cache.modify({
          id: procRef,
          fields: {
            bids(existingRefs: Reference[], { toReference, readField }) {
              return !newBid || existingRefs.some(ref => readField('id', ref) === newBid.id)
                ? existingRefs
                : [
                    ...existingRefs,
                    toReference({
                      __typename: 'BidV2',
                      id: newBid.id
                    })
                  ];
            }
          }
        });
      }
      if (originType === BidItemType.CallOff) {
        const callOffRef = cache.identify({
          __typename: 'UserMail',
          mailId: originId
        });
        cache.modify({
          id: callOffRef,
          fields: {
            bidsV2(existingRefs: Reference[], { toReference, readField }) {
              const newBid = data.createBids.find(bid => bid.itemId === originId);
              return !newBid || existingRefs.some(ref => readField('id', ref) === newBid.id)
                ? existingRefs
                : [
                    ...existingRefs,
                    toReference({
                      __typename: 'BidV2',
                      id: newBid.id
                    })
                  ];
            },
            mailStatus() {
              return EmailStatus.INTERESTING;
            }
          }
        });
      }
    });

    // TODO: ideally we should filter bids references also by origin contained some search match
    // storeFieldName.includes(`search`)
    cache.modify({
      fields: {
        getBids(cached: IBidsCached, { storeFieldName, fieldName, readField, toReference }) {
          if (!storeFieldName.includes(`${wsId}`)) return cached;

          if (
            (newBids[0].status.id && storeFieldName.includes(newBids[0].status.id)) ||
            !storeFieldName.includes('stageIds')
          ) {
            const queryInput: { input?: ISearchBidsRequest } = JSON.parse(storeFieldName.replace(`${fieldName}:`, ''));

            if (!queryInput.input || !notUndefined(queryInput.input.rejected)) return cached;

            const isGetRejectedBidsQuery = queryInput.input.rejected;

            if (isGetRejectedBidsQuery) {
              return {
                ...cached,
                totalBids: sumTotalBidsValue(cached.totalBids, originIds.length)
              };
            } else {
              const newBidsRefs = newBids.map(newBid =>
                toReference({
                  __typename: 'BidV2',
                  id: newBid.id
                })
              );
              const bidsToAdd =
                cached.bids && [...cached.bids].length
                  ? newBidsRefs.filter(
                      newBidsRef =>
                        newBidsRef &&
                        ![...cached.bids].some(
                          bid =>
                            readField({ fieldName: 'id', from: bid }) ===
                            readField({ fieldName: 'id', from: newBidsRef })
                        )
                    )
                  : newBidsRefs;
              return {
                ...cached,
                total: cached.total + bidsToAdd.length,
                totalBids: sumTotalBidsValue(cached.totalBids, bidsToAdd.length),
                bids: [...cached.bids, ...bidsToAdd]
              };
            }
          } else {
            return cached;
          }
        }
      }
    });

    if (origins.length && mpId) {
      cache.modify({
        fields: {
          getTenders(cached: ITendersCached, { storeFieldName, readField }) {
            if (
              ![`${mpId}`, 'isUnhandled'].every(arg => storeFieldName.includes(arg)) ||
              [`${mpId}`, '"isUnhandled":false'].every(arg => storeFieldName.includes(arg))
            )
              return cached;
            return {
              ...cached,
              count: cached.count - origins.length,
              procurementsWithScore: [...cached.procurementsWithScore].filter(
                proc =>
                  proc && !origins.some(originId => originId === readField({ fieldName: 'id', from: proc.procurement }))
              )
            };
          }
        }
      });
    }

    clearWsStatsCache(cache, wsId);
  };
}
interface IUpdateBidsWsResponse {
  __typename: 'Mutation';
  updateBidsWorkspaceV2: IApiShortBid[] & {
    __typename: 'BidV2';
  };
}
interface IUpdateBidsWsRequest {
  workspaceId: string;
  bidsIds: string[];
}
export function useUpdateBidsWs(): MutationTuple<IUpdateBidsWsResponse, IUpdateBidsWsRequest> {
  return useMutation(UPDATE_BIDS_WS);
}

export function getUpdateCacheOnUpdateBidsWs(
  wsId: string,
  bidIds: IBidIdentifier[],
  rejectedView: boolean
): MutationUpdaterFn<IUpdateBidsWsResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const { updateBidsWorkspaceV2: updatedBids } = data;
    if (!updatedBids) {
      return;
    }

    const notInWsBidIds = bidIds.filter(bidIdObj => bidIdObj.wsId !== wsId);

    if (!notInWsBidIds.length) {
      return;
    }

    const groupsByWs = groupBidsByWs(notInWsBidIds);
    cache.modify({
      fields: {
        getBids(cached: IBidsCached, { readField, storeFieldName, fieldName }) {
          if (Object.keys(groupsByWs).every(key => !storeFieldName.includes(key))) return cached;

          const queryInput: { input?: ISearchBidsRequest } = JSON.parse(storeFieldName.replace(`${fieldName}:`, ''));

          const currentWsId = queryInput.input?.wsId;

          if (!currentWsId || !queryInput.input || !notUndefined(queryInput.input.rejected)) {
            return cached;
          }

          if (queryInput.input.rejected !== rejectedView) {
            return {
              ...cached,
              totalBids: subsTotalBidsValue(cached.totalBids, groupsByWs[currentWsId].length)
            };
          } else {
            const filteredBids = cached.bids.filter(
              bid => !groupsByWs[currentWsId].some(bidId => bidId === readField({ fieldName: 'id', from: bid }))
            );

            return {
              ...cached,
              total: cached.total - (cached.bids.length - filteredBids.length),
              totalBids: subsTotalBidsValue(cached.totalBids, groupsByWs[currentWsId].length),
              bids: filteredBids
            };
          }
        }
      }
    });

    cache.modify({
      fields: {
        getBids(cached: IBidsCached, { storeFieldName, toReference, fieldName }) {
          if (!storeFieldName.includes(wsId)) return cached;
          if (
            (updatedBids[0].status?.id && storeFieldName.includes(updatedBids[0].status?.id)) ||
            !storeFieldName.includes('stageIds')
          ) {
            const queryInput: { input?: ISearchBidsRequest } = JSON.parse(storeFieldName.replace(`${fieldName}:`, ''));
            if (!queryInput.input || !notUndefined(queryInput.input.rejected)) return cached;

            if (queryInput.input.rejected !== rejectedView) {
              return {
                ...cached,
                totalBids: sumTotalBidsValue(cached.totalBids, notInWsBidIds.length)
              };
            } else {
              return {
                ...cached,
                total: cached.total + notInWsBidIds.length,
                totalBids: sumTotalBidsValue(cached.totalBids, notInWsBidIds.length),

                bids: [
                  ...cached.bids,
                  ...notInWsBidIds.map(bid => {
                    return toReference({
                      __typename: 'BidV2',
                      id: bid.id
                    });
                  })
                ]
              };
            }
          } else {
            return cached;
          }
        }
      }
    });

    clearWsStatsCache(cache, wsId);
  };
}

interface IUpdateBidStatusResponse {
  __typename: 'Mutation';
  updateBidStatusV2: IApiShortBid & {
    __typename: 'BidV2';
  };
}

interface IUpdateBidsStatusRequest {
  bidId: string;
  statusId: string;
}
export function useUpdateBidStatus(): MutationTuple<IUpdateBidStatusResponse, IUpdateBidsStatusRequest> {
  return useMutation(UPDATE_BID_STATUS);
}
export function getUpdateCacheOnUpdateBidStatus(input: {
  wsId: string;
  bidId: string;
  rejectedView: boolean;
  currentStatusId: string;
  prevStatusId?: string;
}): MutationUpdaterFn<IUpdateBidStatusResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const { wsId, bidId, rejectedView, prevStatusId, currentStatusId } = input;
    const bidRef = cache.identify({
      __typename: 'BidV2',
      id: bidId
    });

    if (currentStatusId && bidRef) {
      cache.modify({
        fields: {
          getBids(cached: IBidsCached, { storeFieldName, toReference, readField, fieldName }) {
            const queryInput: { input?: ISearchBidsRequest } = JSON.parse(storeFieldName.replace(`${fieldName}:`, ''));

            if (!queryInput.input || !notUndefined(queryInput.input.rejected)) return cached;

            const queryOnCurrentView = queryInput.input.rejected === rejectedView;

            if (storeFieldName.includes(currentStatusId)) {
              if (queryOnCurrentView) {
                return {
                  ...cached,
                  total: cached.total + 1,
                  totalBids: sumTotalBidsValue(cached.totalBids, 1),
                  bids: [...cached.bids, toReference(bidRef)]
                };
              } else {
                return { ...cached, totalBids: sumTotalBidsValue(cached.totalBids, 1) };
              }
            } else if (prevStatusId && storeFieldName.includes(prevStatusId)) {
              const newBids = [...cached.bids].filter(
                bid => bid && bidId !== readField({ fieldName: 'id', from: bid })
              );

              if (queryOnCurrentView) {
                return {
                  ...cached,
                  total: cached.total - 1,
                  totalBids: subsTotalBidsValue(cached.totalBids, 1),
                  bids: newBids
                };
              } else {
                return {
                  ...cached,
                  totalBids: subsTotalBidsValue(cached.totalBids, 1)
                };
              }
            } else {
              return cached;
            }
          }
        }
      });
    }

    clearWsStatsCache(cache, wsId);
  };
}

interface IMoveToWorkspaceInput {
  workspaceId: string;
  eventSource: string;
  originType?: BidItemType;
  originIds?: string[];
  bids?: IBidIdentifier[];
  linkedBids?: ILinkedBidIdentifier[];
}

export function useMoveToWorkspace(): [(data: IMoveToWorkspaceInput) => Promise<void>, { loading: boolean }] {
  const [createBids, { loading: createBidLoading }] = useCreateBids();
  const [updateBidsWs, { loading: updateBidsWsLoading }] = useUpdateBidsWs();
  const [unassignMailsToBid, { loading: unassignLoading }] = useUnAssignMailsToBid();
  const [{ rejected: isRejectedTab }] = useBidsSearchArgs();
  const [activeMpId] = useActiveMpIdState();
  const loading = createBidLoading || updateBidsWsLoading || unassignLoading;
  const { t } = useTranslation();

  const moveToWorkspaceFn = useCallback(
    async (data: IMoveToWorkspaceInput) => {
      const { workspaceId, eventSource, originType, originIds, bids, linkedBids } = data;
      try {
        if (!!originType && originType === BidItemType.CallOff && linkedBids) {
          const groups = groupBy(linkedBids, 'id');
          const promises = Object.entries(groups).map(([key, val]) => {
            return unassignMailsToBid({
              variables: { bidId: key, mailIds: val.map(bid => bid.originId) }
            });
          });
          await Promise.all(promises);
        }
        if (!!originType && !!originIds && !!originIds.length) {
          await createBids({
            variables: {
              workspaceId: workspaceId,
              items: originIds.map(id => ({ id })),
              itemType: originType
            },
            update: getUpdateCacheOnCreateBids(originIds, originType, workspaceId, activeMpId)
          });
          trackMoveItemsToWorkspace(originIds, originType, workspaceId, eventSource, activeMpId);
        }
        if (!!bids && !!bids.length) {
          const bidsIds = bids.map(bid => bid.id);
          await updateBidsWs({
            variables: {
              workspaceId,
              bidsIds
            },
            update: getUpdateCacheOnUpdateBidsWs(workspaceId, bids, !!isRejectedTab)
          });
        }
      } catch {
        notification.error({
          message: t('Common.unknownError'),
          description: t('Common.unknownErrorDesc')
        });
      }
    },
    [activeMpId, createBids, isRejectedTab, t, unassignMailsToBid, updateBidsWs]
  );
  return [
    moveToWorkspaceFn,
    {
      loading
    }
  ];
}

function sumTotalBidsValue(currentVal: number | undefined, term: number): number | undefined {
  return notUndefined(currentVal) ? currentVal + term : undefined;
}

function subsTotalBidsValue(currentVal: number | undefined, term: number): number | undefined {
  return notUndefined(currentVal) ? currentVal - term : undefined;
}

export function useUpdateWsSorting(): [(sorting: IBidSorting) => void, { loading: boolean; error?: ApolloError }] {
  const [setRequestSettings, { loading, error }] = useSetRequestSettings();
  const [wsId] = useActiveWsIdState();

  const updateWsSortingFn = useCallback(
    (sorting: IBidSorting) => {
      wsId && setRequestSettings({ entityId: wsId, type: RequestType.BidsSorting, settings: JSON.stringify(sorting) });
    },
    [wsId, setRequestSettings]
  );
  return useMemo(() => [updateWsSortingFn, { loading, error }], [updateWsSortingFn, error, loading]);
}
